import axios from 'axios';

const CONFIG = require('../config/urls.js');
const API_BASE_URL = process.env.REACT_APP_API_URL || CONFIG.BACKEND.API_BASE_URL;

class WebsiteAPI {
  private api = axios.create({
    baseURL: `${API_BASE_URL}/website`,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store',
    },
  });

  constructor() {
    // Request interceptor - Add JWT token from localStorage
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle 401 Unauthorized
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('Unauthorized access detected in WebsiteAPI - logging out');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.assign('/');
        }
        return Promise.reject(error);
      }
    );
  }

  // Pricing Plans API
  async getPricingPlans(category?: 'home' | 'enterprise' | 'retail') {
    const params = category ? { category } : {};
    const response = await this.api.get('/pricing-plans', { params });
    return response.data.plans;
  }

  async createPricingPlan(planData: any) {
    const response = await this.api.post('/pricing-plans', planData);
    return response.data;
  }

  async updatePricingPlan(id: string, planData: any) {
    const response = await this.api.put(`/pricing-plans/${id}`, planData);
    return response.data;
  }

  async deletePricingPlan(id: string) {
    const response = await this.api.delete(`/pricing-plans/${id}`);
    return response.data;
  }

  async togglePricingPlanStatus(id: string, isActive: boolean) {
    const response = await this.api.patch(`/pricing-plans/${id}/toggle-status`, { isActive });
    return response.data;
  }

  // Website Content API
  async getWebsiteContent() {
    const response = await this.api.get('/content');
    return response.data.content;
  }

  async createContent(contentData: any) {
    const response = await this.api.post('/content', contentData);
    return response.data;
  }

  async updateContent(id: string, contentData: any) {
    const response = await this.api.put(`/content/${id}`, contentData);
    return response.data;
  }

  async deleteContent(id: string) {
    const response = await this.api.delete(`/content/${id}`);
    return response.data;
  }

  // Payment Settings API
  async getPaymentSettings() {
    const response = await this.api.get('/payment-settings');
    return response.data.settings;
  }

  async updatePaymentSettings(settingsData: any) {
    try {
      const response = await this.api.put('/payment-settings', settingsData);
      return response.data;
    } catch (error) {
      console.error('Payment settings update error:', error);
      this.handleError(error);
    }
  }

  // Audit Logs API
  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    action?: string;
    resource?: string;
    success?: boolean;
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
  }) {
    const response = await this.api.get('/audit-logs', { params });
    return response.data;
  }

  async createAuditLog(logData: any) {
    const response = await this.api.post('/audit-logs', logData);
    return response.data;
  }

  // Inuka Users API
  async getInukaUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    subscription_status?: string;
  }) {
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  async getInukaUser(id: string) {
    const response = await this.api.get(`/users/${id}`);
    return response.data.user;
  }

  async updateInukaUser(id: string, userData: any) {
    const response = await this.api.put(`/users/${id}`, userData);
    return response.data;
  }

  async createInukaUser(userData: any) {
    const response = await this.api.post('/users', userData);
    return response.data;
  }

  async bulkDeleteInukaUsers(userIds: string[], deleteFingerprints: boolean = false) {
    const response = await this.api.post('/users/bulk-delete', {
      user_ids: userIds,
      delete_fingerprints: deleteFingerprints
    });
    return response.data;
  }

  async deleteInukaUser(id: string, deleteFingerprint: boolean = false) {
    const response = await this.api.delete(`/users/${id}`, {
      params: { delete_fingerprint: deleteFingerprint }
    });
    return response.data;
  }

  async syncUserToFingerprint(id: string) {
    const response = await this.api.post(`/users/${id}/sync-fingerprint`);
    return response.data;
  }

  async syncAllUsersToFingerprints() {
    const response = await this.api.post('/users/sync-all');
    return response.data;
  }

  // Blogs API
  async getBlogs(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    is_published?: boolean;
  }) {
    const response = await this.api.get('/blogs', { params });
    return response.data;
  }

  async getBlog(id: string) {
    const response = await this.api.get(`/blogs/${id}`);
    return response.data.blog;
  }

  async createBlog(blogData: any) {
    const response = await this.api.post('/blogs', blogData);
    return response.data;
  }

  async updateBlog(id: string, blogData: any) {
    const response = await this.api.put(`/blogs/${id}`, blogData);
    return response.data;
  }

  async deleteBlog(id: string) {
    const response = await this.api.delete(`/blogs/${id}`);
    return response.data;
  }

  async toggleBlogPublish(id: string, isPublished: boolean) {
    const response = await this.api.patch(`/blogs/${id}/publish`, { isPublished });
    return response.data;
  }

  // Error handling
  private handleError(error: any) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data.message || 'Server error occurred');
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error - please check your connection');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
}

export default new WebsiteAPI();

